'use client';

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

// --- Types ---

// --- Types ---

interface Message {
    id: string;
    text: string;
    sender: 'bot' | 'user';
    type: 'text' | 'options' | 'input' | 'typing';
    options?: string[];
}

interface FormData {
    name: string;
    email: string;
    mobile: string;
    service: string;
    budget: string;
    overview: string;
    company: string; // Hidden field for compatibility
    domain: string; // Hidden field for compatibility
}

interface StepConfig {
    id: string;
    field: keyof FormData;
    questions: (string | ((name: string) => string))[];
    inputType: 'text' | 'email' | 'tel' | 'options' | 'textarea';
    placeholder?: string;
    validation?: (val: string) => boolean;
    options?: string[];
    optional?: boolean;
}

const MobileContactChat: React.FC = () => {
    // --- State ---
    const [step, setStep] = useState(0);
    const [messages, setMessages] = useState<Message[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        mobile: '',
        service: '',
        budget: '',
        overview: '',
        company: '',
        domain: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // --- Configuration ---
    const serviceOptions = [
        'Website Development',
        'Mobile App Development',
        'Brand Visibility (SEO)',
        'E-commerce',
        'UI/UX Design',
        'Other'
    ];

    const budgetOptions = [
        '₹30k - ₹50k',
        '₹50k - ₹1L',
        '₹1L - ₹2.5L',
        '₹2.5L+',
        'Let\'s Discuss'
    ];

    const steps = [
        {
            id: 'name',
            field: 'name',
            questions: ["Hi there! 👋 I'm the Sitemap Genius Assistant.", "I can help you get your project started. First, what's your name?"],
            inputType: 'text',
            placeholder: 'Type your name...'
        },
        {
            id: 'email',
            field: 'email',
            questions: [(name: string) => `Nice to meet you, ${name}! What's the best email to reach you?`],
            inputType: 'email',
            placeholder: 'name@example.com',
            validation: (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
        },
        {
            id: 'mobile',
            field: 'mobile',
            questions: ["Got it. And your mobile number?"],
            inputType: 'tel',
            placeholder: '+91 99999 99999',
            validation: (val: string) => /^[+]?[0-9\s-]{10,15}$/.test(val)
        },
        {
            id: 'company',
            field: 'company',
            questions: ["Do you represent a company? If so, what is the company name?"],
            inputType: 'text',
            placeholder: 'Company name (or tap Skip)',
            optional: true // New property
        },
        {
            id: 'service',
            field: 'service',
            questions: ["How can sitemap genius help you today?"], // Updated text
            inputType: 'options',
            options: serviceOptions
        },
        {
            id: 'budget',
            field: 'budget',
            questions: ["Do you have a budget range in mind?"],
            inputType: 'options',
            options: budgetOptions
        },
        {
            id: 'overview',
            field: 'overview',
            questions: ["Last question! Briefly describe your idea or requirements."],
            inputType: 'textarea',
            placeholder: 'Tell us a bit about your project...'
        }
    ];

    // --- Effects ---

    // Initial Start
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (!hasInitialized.current && messages.length === 0) {
            hasInitialized.current = true;
            triggerBotMessage(0);
        }
    }, [messages.length]);

    // Auto-scroll
    // Auto-scroll
    useEffect(() => {
        // Only scroll if we have messages and it's not the initial load (or check if user interacted)
        // For simplicity, just scrolling the container to bottom is better than scrollIntoView which might hijack page scroll
        if (messagesEndRef.current?.parentElement) {
            const container = messagesEndRef.current.parentElement;
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [messages, isTyping]);


    // --- Logic ---

    const triggerBotMessage = async (stepIndex: number) => {
        setIsTyping(true);
        const currentStep = steps[stepIndex];

        // Process multiple messages if array
        const qs = currentStep.questions;

        for (let i = 0; i < qs.length; i++) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Natural typing delay

            const qContent = qs[i];
            const text = typeof qContent === 'function' ? qContent(formData.name) : qContent;

            addMessage({
                id: `bot-${stepIndex}-${i}`,
                text,
                sender: 'bot',
                type: 'text'
            });
        }

        setIsTyping(false);

        // If options, show them immediately after
        if (currentStep.inputType === 'options') {
            // Options are rendered in the input area, not as a message bubble
        }
    };

    const addMessage = (msg: Message) => {
        setMessages(prev => [...prev, msg]);
    };

    const handleSend = async (text: string = inputValue) => {
        // Allow SKIP if optional and empty
        const currentStep = steps[step];
        const isSkip = !text.trim() && currentStep?.optional;

        if (!text.trim() && !isSkip) return;

        const finalText = isSkip ? "Skipped" : text;

        // Validation
        const stepValidation = currentStep.validation; // Renamed to avoid collision
        if (stepValidation && !stepValidation(text)) {
            // Show error toast or temporary bot message? 
            // For simplicity, let's add a temporary bot error message
            addMessage({
                id: `bot-err-${Date.now()}`,
                text: "Please enter a valid format. 🙏",
                sender: 'bot',
                type: 'text'
            });
            return;
        }

        // Add User Message
        addMessage({
            id: `user-${step}`,
            text,
            sender: 'user',
            type: 'text'
        });
        setInputValue('');

        // Update Data
        const newData = { ...formData, [currentStep.field]: text };
        setFormData(newData);

        // Next Step
        if (step < steps.length - 1) {
            setStep(prev => prev + 1);
            triggerBotMessage(step + 1);
        } else {
            // Submit Form
            handleFinalSubmit(newData);
        }
    };

    const handleFinalSubmit = async (finalData: FormData) => {
        setIsSubmitting(true);
        setIsTyping(true);

        try {
            const response = await fetch('https://sitemapgenius.com/backend/contact.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: finalData.name,
                    email: finalData.email,
                    mobile: finalData.mobile,
                    company: '',
                    website: '',
                    service: finalData.service,
                    budget: finalData.budget,
                    message: finalData.overview
                }),
            });

            const result = await response.json();
            setIsTyping(false);

            if (result.success) {
                setSubmitStatus('success');
                addMessage({
                    id: 'final-success',
                    text: 'Perfect! I\'ve sent your details to our team.  We\'ll be in touch within 24-48 hours.',
                    sender: 'bot',
                    type: 'text'
                });
            } else {
                setSubmitStatus('error');
                addMessage({
                    id: 'final-error',
                    text: 'Oops! Something went wrong sending your message. Please try again or email us directly.',
                    sender: 'bot',
                    type: 'text'
                });
            }

        } catch (error) {
            console.error(error);
            setIsTyping(false);
            addMessage({
                id: 'final-error-net',
                text: 'Network error. Please check your connection.',
                sender: 'bot',
                type: 'text'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentStepConfig = steps[step];
    const isOptionsStep = currentStepConfig?.inputType === 'options';

    return (
        // Wrapper for Sticky Effect (replaces GSAP Pinning)
        <div className="relative min-h-[130vh] lg:hidden mb-12">
            <div ref={containerRef} className="sticky top-20 h-[80vh] bg-slate-50 flex flex-col relative overflow-hidden border-t border-slate-100 rounded-3xl shadow-inner mx-2">
                {/* Header */}
                <div className="bg-white px-4 py-3 border-b border-slate-100 flex items-center gap-3 shadow-sm z-10">
                    <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                            <Icon name="ChatBubbleLeftRightIcon" className="w-5 h-5" />
                        </div>
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-900">Sitemap Genius Assistant</h3>
                        <p className="text-xs text-green-600 font-medium">Online Now</p>
                    </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg) => (
                        <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed shadow-sm ${msg.sender === 'user'
                                    ? 'bg-indigo-600 text-white rounded-2xl rounded-tr-none'
                                    : 'bg-white text-slate-700 border border-slate-100 rounded-2xl rounded-tl-none'
                                    }`}
                            >
                                {msg.text}
                            </div>
                        </motion.div>
                    ))}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex justify-start"
                        >
                            <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-none px-4 py-4 shadow-sm flex gap-1 items-center">
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></span>
                            </div>
                        </motion.div>
                    )}

                    {/* Spacer for bottom input */}
                    <div ref={messagesEndRef} className="h-4" />
                </div>

                {/* Input Area */}
                <div className="bg-white p-4 border-t border-slate-100">
                    {submitStatus === 'success' ? (
                        <div className="p-4 bg-green-50 rounded-2xl border border-green-100">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white shadow-sm shrink-0">
                                    <Icon name="CheckIcon" className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-green-900 text-lg">Message Received!</h4>
                                    <p className="text-xs text-green-700">We've started processing your request.</p>
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="relative pl-6 border-l-2 border-green-200/60 ml-6 space-y-6 py-1 mb-2">
                                {/* Item 1 */}
                                <div className="relative">
                                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-green-500 ring-4 ring-green-100"></div>
                                    <p className="text-sm font-bold text-slate-800">Inquiry Logged</p>
                                    <p className="text-xs text-slate-500 font-medium">Just now</p>
                                </div>
                                {/* Item 2 */}
                                <div className="relative">
                                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-indigo-500 ring-4 ring-indigo-50 animate-pulse"></div>
                                    <p className="text-sm font-bold text-slate-800">Expert Review</p>
                                    <p className="text-xs text-indigo-600 font-medium animate-pulse">In Progress...</p>
                                </div>
                                {/* Item 3 */}
                                <div className="relative">
                                    <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-slate-200 ring-4 ring-slate-50"></div>
                                    <p className="text-sm font-bold text-slate-400">Response & Proposal</p>
                                    <p className="text-xs text-slate-400 font-medium">Within 48 Hours</p>
                                </div>
                            </div>

                            <button
                                onClick={() => window.location.reload()}
                                className="mt-6 w-full py-3 bg-white border border-green-200 text-green-700 font-bold rounded-xl text-sm hover:bg-green-50 transition-colors shadow-sm"
                            >
                                Start New Chat
                            </button>
                        </div>
                    ) : isOptionsStep ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                            {currentStepConfig.options?.map((opt) => (
                                <button
                                    key={opt}
                                    onClick={() => handleSend(opt)}
                                    className="w-full px-5 py-4 rounded-xl bg-white text-slate-700 text-sm font-semibold transition-all border border-slate-200 shadow-sm hover:border-indigo-500 hover:text-indigo-600 hover:shadow-md hover:bg-indigo-50 active:scale-95 text-left flex items-center justify-between group"
                                >
                                    {opt}
                                    <Icon name="ChevronRightIcon" className="w-4 h-4 text-slate-300 group-hover:text-indigo-500" />
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            {currentStepConfig?.inputType === 'textarea' ? (
                                <textarea
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={currentStepConfig?.placeholder}
                                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-sm resize-none"
                                    rows={1}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                />
                            ) : (
                                <input
                                    ref={inputRef}
                                    type={currentStepConfig?.inputType || 'text'}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={currentStepConfig?.placeholder}
                                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-sm"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                />
                            )}
                            <button
                                onClick={() => handleSend()}
                                disabled={!inputValue.trim() && !currentStepConfig?.optional}
                                className={`min-w-[48px] h-[48px] rounded-xl flex items-center justify-center transition-all shadow-lg ${!inputValue.trim() && currentStepConfig?.optional
                                    ? 'bg-slate-100 text-slate-500 shadow-none border border-slate-200'
                                    : 'bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700'
                                    }`}
                            >
                                {(!inputValue.trim() && currentStepConfig?.optional) ? (
                                    <span className="text-xs font-bold">SKIP</span>
                                ) : (
                                    <Icon name="PaperAirplaneIcon" className="w-5 h-5 -rotate-45 translate-x-0.5" />
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MobileContactChat;
